<template>
  <!-- Rendered once by AccountLayout above the page, so it stays mounted while the page
       below it changes. The route meta describes the bar (title, back link, greeting); a
       page adds its own controls through AppTopBarContent, teleported into the targets below. -->
  <v-toolbar
    ref="barRef"
    flat
    border="b"
    class="top-bar app-top-bar sc-top-bar"
    :class="{ 'sc-top-bar--mobile': display.xs.value }"
    :extension-height="content.extensionHeight"
  >
    <v-app-bar-nav-icon v-if="showMenuButton" @click="toggleMobileMenu" />

    <v-toolbar-title v-if="isWelcome" class="app-top-bar__page-title">
      <span>Welcome back, {{ userFullName }}</span>
    </v-toolbar-title>
    <v-toolbar-title v-else-if="listName" class="app-top-bar__page-title">
      <router-link
        class="pa-0 v-btn justify-start"
        :style="`font-size: ${display.xs.value ? '10px' : '0.75rem' }`"
        color="primary"
        :to="getBackToListRoute()"
      >
        <span style="text-transform: capitalize;"> All {{ listName }}</span>
      </router-link>
      <div :style="`font-size: ${display.xs.value ? '16px' : '1.25rem' }`">
        {{ metaTitle }}
      </div>
    </v-toolbar-title>
    <template v-else>
      <v-btn
        v-if="backTo"
        :class="display.smAndUp.value ? 'pl-1' : 'ml-0 px-1 pr-3'"
        size="small"
        variant="text"
        border
        :to="getAccountRoute({ name: backTo.name })"
      >
        <v-icon class="ml-0">mdi-chevron-left</v-icon>
        Back{{ display.smAndUp.value ? ` to ${backTo.label}` : '' }}
      </v-btn>
      <v-toolbar-title v-if="metaTitle" :class="backTo ? 'ml-4' : 'ml-0'">
        {{ metaTitle }}
      </v-toolbar-title>
    </template>

    <v-spacer />
    <!-- v-show: an empty target would still take a gap -->
    <div v-show="content.hasExtension && !display.xs.value" :id="APP_TOP_BAR_EXTENSION_ID" class="app-top-bar__extension d-flex align-center ga-2" />
    <v-spacer v-if="content.hasExtension && !display.xs.value" />
    <div v-show="content.hasActions" :id="APP_TOP_BAR_ACTIONS_ID" class="d-flex align-center ga-2" />
    <v-chip v-if="$devmode" label class="dev-mode-bar px-2">
      {{ display.smAndUp.value ? 'Development Mode' : 'Dev' }}
    </v-chip>
    <AiAgentButton />
    <v-divider
      v-if="$devmode"
      vertical
      :class="display.smAndUp.value ? 'mx-2' : 'mx-0'"
    />
    <ProfileMenuWrapper />

    <template v-if="content.hasBelowExtension" #extension>
      <div :id="APP_TOP_BAR_EXTENSION_BELOW_ID" class="d-flex align-center w-100" />
    </template>
  </v-toolbar>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, useTemplateRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useDisplay } from 'vuetify';

import { RootMutations } from '@/store/types/mutation-types';
import { StoreModulesNamesEnum } from '@/store/StoreModulesNamesEnum';
import { IAppTopBarBackTo } from '@/bundles/App/types';
import { IAppRouteMeta } from '@/router/types/routeMeta';
import { IGetAccountRouteHandler } from '@/bundles/App/helpers';
import { appPanelName, sourceListRouteName } from '@/bundles/Panels/helpers';
import { useAccountRoute } from '@/bundles/App/composables/useAccountRoute';
import {
  APP_TOP_BAR_ACTIONS_ID,
  APP_TOP_BAR_EXTENSION_BELOW_ID,
  APP_TOP_BAR_EXTENSION_ID,
  useAppTopBarContent,
} from '@/bundles/App/composables/useAppTopBar';
import { eventBus } from '@/eventbus/eventbus';

import AiAgentButton from '@/bundles/AiAgent/components/AiAgentButton.vue';
import ProfileMenuWrapper from '@/bundles/Panels/components/ProfileMenuWrapper.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const display = useDisplay();
const content = useAppTopBarContent();
const { getAccountRoute } = useAccountRoute();

// Nearest match wins, so a branch sets the default and a single route overrides it
function findMeta<K extends keyof IAppRouteMeta> (key: K): IAppRouteMeta[K] | undefined {
  return [...route.matched].reverse().find(record => record.meta[key] !== undefined)?.meta[key];
}

const isWelcome = computed<boolean>(() => !!findMeta('topBarWelcome'));
const source = computed<string>(() => findMeta('topBarSource') ?? '');
const listName = computed<string | undefined>(() => appPanelName[source.value]);
const userFullName = computed<string>(() => store.state.user?.name || store.state.user?.profile?.first_name || '');

const backTo = computed<IAppTopBarBackTo | null>(() => (findMeta('hideBack')
  ? null
  : findMeta('backTo') ?? null));

const metaTitle = computed<string>(() => findMeta('topBarTitle') ?? route.meta.title ?? '');

const showMenuButton = computed<boolean>(() => display.xs.value && !findMeta('hideMenuButton'));

const toggleMobileMenu = (): void => {
  store.commit(RootMutations.SET_NAV_STATE, !store.state.nav);
};

// Back to the list the record was opened from, with the set or filter that was active
const getBackToListRoute = (): IGetAccountRouteHandler => {
  const name = sourceListRouteName[source.value];
  const lastSelectedType = store.getters[`${StoreModulesNamesEnum.GroupFilters}/getLastSelectedType`](source.value);

  switch (lastSelectedType) {
    case 'savedSet':
    case 'defaultFilter':
      return getAccountRoute({ name, query: { set: store.getters[`${StoreModulesNamesEnum.GroupFilters}/getFilterId`](source.value) } });
    case 'all':
      return getAccountRoute({ name, query: { allRecords: 'true' } });
    default:
      return getAccountRoute({ name });
  }
};

const goBackToList = (): void => {
  if (!listName.value) return;
  router.push(getBackToListRoute());
};

// The page below fills the height the bar leaves (`.v-main.app-shell > .page`), and the
// bar grows with an extension row, so it publishes its live height
const barRef = useTemplateRef<{ $el: HTMLElement }>('barRef');
const heightObserver = new ResizeObserver(([entry]) => {
  document.documentElement.style.setProperty('--app-top-bar-height', `${Math.round(entry.borderBoxSize[0].blockSize)}px`);
});

onMounted(() => {
  eventBus.$on('go:back-to-list', goBackToList);
  if (barRef.value) heightObserver.observe(barRef.value.$el);
});
onBeforeUnmount(() => {
  eventBus.$off('go:back-to-list', goBackToList);
  heightObserver.disconnect();
});
</script>

<style lang="scss" scoped>
// everything in the bar, the page's own controls included, keeps the same spacing
.app-top-bar :deep(.v-toolbar__content) {
  gap: 8px;
}

// the inline extension may shrink when the bar is narrow, so its content can too
.app-top-bar__extension {
  min-width: 0;
}

// a page title starts at the bar's edge even after the mobile menu icon
.app-top-bar__page-title:not(:first-child) {
  margin-left: 0 !important;
}

// the title keeps its one line; the page's extension gives up width instead (the toolbar
// suffix outranks the global `.sc-top-bar .v-toolbar-title { flex: 0 1 auto }`)
.app-top-bar .app-top-bar__page-title.v-toolbar-title {
  flex: none;
}
</style>
